import React, { useMemo, useState } from 'react';

import type { InsertCrossReferenceTarget } from '@aline/common';
import { Queries } from '@aline/common';
import type { SelectChangeEvent } from '@mui/material';
import { Button, DialogActions, FormControl, FormLabel, MenuItem, Select } from '@mui/material';
import { Box } from '@mui/system';
import clsx from 'clsx';
import { Range } from 'slate';
import { AlineDialog } from 'src/components';
import { DownArrowIconSize12 } from 'src/components/icons/DownArrowIconSize12';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor } from 'src/layout-engine';
import { NotificationsActionType, useNotificationDispatch } from 'src/notifications';
import { useEditingMode } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

import type { ReferenceType } from './cross-reference-options';
import {
  EMPTY_TARGET_LABELS,
  INSERT_REFERENCE_TO_OPTIONS,
  isReferenceType,
  REFERENCE_TYPES,
} from './cross-reference-options';
import type { CrossReferenceTarget } from './cross-reference-targets';
import { useCrossReferenceTargets } from './use-cross-reference-targets';

interface CrossReferenceDialogProps {
  handleClose: () => void;
  open: boolean;
}

const DIALOG_WIDTH_PX = 640;
const DIALOG_HEIGHT_PX = 352;
const DIALOG_MENU_HEIGHT_PX = 320;
const DIALOG_HORIZONTAL_PADDING_PX = 24;
const SELECT_MENU_WIDTH_PX = DIALOG_WIDTH_PX - DIALOG_HORIZONTAL_PADDING_PX * 2;

const SELECT_HEIGHT_PX = 42;
const SELECT_ICON_TOP_OFFSET_PX = 6;

const TARGET_INDENT_BASE = 2;
const TARGET_INDENT_PER_LEVEL = 2;

const OUTLINED_BORDER_COLOR = 'rgba(0, 0, 0, 0.23)';

const SELECT_MENU_PROPS = {
  slotProps: {
    paper: {
      sx: {
        '& .MuiMenuItem-root': {
          display: 'block',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        maxHeight: DIALOG_MENU_HEIGHT_PX,
        maxWidth: 'calc(100vw - 64px)',
        width: `min(${SELECT_MENU_WIDTH_PX}px, calc(100vw - 80px))`,
      },
    },
  },
};

const useStyles = makeStyles()(theme => ({
  label: {
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  menuItem: {
    display: 'flex !important',
  },
  paper: {
    maxWidth: 'calc(100vw - 32px)',
    minHeight: DIALOG_HEIGHT_PX,
    width: DIALOG_WIDTH_PX,
  },
  placeholder: {
    color: theme.appColors.placeholderType,
  },
  preview: {
    color: theme.appColors.secondaryType,
    marginLeft: 'auto',
  },
  select: {
    '& .MuiSelect-icon': {
      marginRight: theme.spacing(1),
      marginTop: SELECT_ICON_TOP_OFFSET_PX,
      transform: 'none !important',
    },
    '&:not(.Mui-disabled):hover': {
      '&:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${OUTLINED_BORDER_COLOR}`,
      },
      backgroundColor: theme.appColors.hoverBackground,
    },
    alignItems: 'center',
    height: SELECT_HEIGHT_PX,
  },
}));

export function CrossReferenceDialog({ handleClose, open }: CrossReferenceDialogProps) {
  const { classes } = useStyles();
  const editor = useEditor();
  const dispatch = useNotificationDispatch();
  const currentUserId = useCurrentUserId();
  const { editing, editingMode, suggesting } = useEditingMode();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const canInsert = editing || suggesting;

  const [referenceType, setReferenceType] = useState<ReferenceType | ''>('');
  const [insertReferenceTo, setInsertReferenceTo] = useState('');
  const [target, setTarget] = useState('');

  const { targets } = useCrossReferenceTargets(referenceType, open);

  const insertReferenceToOptions = referenceType ? INSERT_REFERENCE_TO_OPTIONS[referenceType] : [];
  const selectedOption =
    insertReferenceToOptions.find(option => option.key === insertReferenceTo) ?? null;
  const selectedTarget = targets.find(item => item.key === target) ?? null;
  const canSave = Boolean(selectedTarget && selectedOption && currentUserId && canInsert);

  const targetIsNumbered = useMemo(
    () =>
      selectedTarget
        ? Queries.getIsNumberedTarget(editor, getCrossReferenceTarget(selectedTarget))
        : false,
    [editor, selectedTarget]
  );

  const referenceTypeLabel = REFERENCE_TYPES.find(item => item.key === referenceType)?.label ?? '';

  const insertReferenceToPreviews: Map<string, string> = useMemo(() => {
    const fieldPoint = editor.selection ? Range.start(editor.selection) : null;
    if (!referenceType || !selectedTarget || !fieldPoint) return new Map();

    const previewTarget = getCrossReferenceTarget(selectedTarget);
    return new Map(
      INSERT_REFERENCE_TO_OPTIONS[referenceType].map(option => [
        option.key,
        Queries.previewCrossReferenceText(editor, {
          fieldPoint,
          instruction: option.instruction,
          switches: option.switches,
          target: previewTarget,
        }),
      ])
    );
  }, [editor, referenceType, selectedTarget]);

  function handleReferenceTypeChange(event: SelectChangeEvent) {
    const nextReferenceType = event.target.value;
    setReferenceType(isReferenceType(nextReferenceType) ? nextReferenceType : '');
    setInsertReferenceTo('');
    setTarget('');
  }

  function handleInsertReferenceToChange(event: SelectChangeEvent) {
    setInsertReferenceTo(event.target.value);
  }

  function handleTargetChange(event: SelectChangeEvent) {
    setTarget(event.target.value);
  }

  function handleSave() {
    if (!selectedOption || !selectedTarget || !currentUserId || !editingMode || !canInsert) return;

    const inserted = AuthorizedTransforms.insertCrossReference(editor, {
      authorId: currentUserId,
      editingMode,
      instruction: selectedOption.instruction,
      switches: selectedOption.switches,
      target: getCrossReferenceTarget(selectedTarget),
    });

    if (inserted) {
      handleClose();
      return;
    }

    dispatch({
      payload: {
        message: 'Could not insert the cross reference. Please try again.',
        variant: 'error',
      },
      type: NotificationsActionType.PUSH_NOTIFICATION,
    });
  }

  function handleExited() {
    setReferenceType('');
    setInsertReferenceTo('');
    setTarget('');
    ClientTransforms.focus(editor);
  }

  return (
    <AlineDialog
      disableRestoreFocus
      classes={{ paper: classes.paper }}
      maxWidth={false}
      open={open}
      slotProps={{ transition: { onExited: handleExited } }}
      title="Insert Cross-reference"
      onClose={handleClose}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          width: '100%',
        }}
      >
        <CrossReferenceSelect
          displayLabel={referenceTypeLabel}
          id="reference-type"
          label="Reference Type"
          value={referenceType}
          onChange={handleReferenceTypeChange}
        >
          {REFERENCE_TYPES.map(item => (
            <MenuItem key={item.key} className={classes.menuItem} value={item.key}>
              {item.label}
            </MenuItem>
          ))}
        </CrossReferenceSelect>

        <CrossReferenceSelect
          disabled={!referenceType}
          displayLabel={selectedTarget?.label ?? ''}
          id="destination"
          label="Destination"
          value={selectedTarget?.key ?? ''}
          onChange={handleTargetChange}
        >
          {targets.length === 0 ? (
            <MenuItem disabled className={classes.menuItem} value="">
              {referenceType ? EMPTY_TARGET_LABELS[referenceType] : 'No targets found'}
            </MenuItem>
          ) : (
            targets.map(item => (
              <MenuItem
                key={item.key}
                className={classes.menuItem}
                sx={{ pl: TARGET_INDENT_BASE + item.level * TARGET_INDENT_PER_LEVEL }}
                value={item.key}
              >
                {item.label}
              </MenuItem>
            ))
          )}
        </CrossReferenceSelect>

        <CrossReferenceSelect
          disabled={!referenceType || !selectedTarget}
          displayLabel={selectedOption?.label ?? ''}
          id="text-to-display"
          label="Text to Display"
          value={insertReferenceTo}
          onChange={handleInsertReferenceToChange}
        >
          {insertReferenceToOptions
            // Hide number options for targets that aren't in a numbered list.
            .filter(option => targetIsNumbered || !isNumberOption(option))
            .map(option => {
              const preview = insertReferenceToPreviews.get(option.key);
              return (
                <MenuItem key={option.key} className={classes.menuItem} value={option.key}>
                  {option.label}
                  {preview && <span className={classes.preview}>{preview}</span>}
                </MenuItem>
              );
            })}
        </CrossReferenceSelect>
      </Box>
      <DialogActions sx={{ justifyContent: 'flex-end', pt: 3, px: 0 }}>
        <Button
          sx={{
            borderRadius: 999,
            px: 2,
            textTransform: 'none',
          }}
          variant="outlined"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          disabled={!canSave}
          sx={{
            borderRadius: 999,
            px: 4,
            textTransform: 'none',
          }}
          variant="contained"
          onClick={handleSave}
        >
          Save
        </Button>
      </DialogActions>
    </AlineDialog>
  );
}

interface CrossReferenceSelectProps {
  children: React.ReactNode;
  displayLabel: string;
  id: string;
  label: string;
  onChange: (event: SelectChangeEvent) => void;
  value: string;
  disabled?: boolean;
}

function CrossReferenceSelect({
  children,
  disabled,
  displayLabel,
  id,
  label,
  onChange,
  value,
}: CrossReferenceSelectProps) {
  const { classes } = useStyles();

  return (
    <FormControl>
      <FormLabel className={classes.label} htmlFor={id} id={`${id}-label`}>
        {label}
      </FormLabel>
      <Select
        displayEmpty
        fullWidth
        IconComponent={DownArrowIconSize12}
        MenuProps={SELECT_MENU_PROPS}
        className={clsx(classes.select, !value && classes.placeholder)}
        disabled={disabled}
        id={id}
        labelId={`${id}-label`}
        renderValue={() => displayLabel || label}
        value={value}
        onChange={onChange}
      >
        {children}
      </Select>
    </FormControl>
  );
}

function isNumberOption(option: { switches: string }): boolean {
  return /\\[nrw]/.test(option.switches);
}

function getCrossReferenceTarget(selectedTarget: CrossReferenceTarget): InsertCrossReferenceTarget {
  if (selectedTarget.kind === 'bookmark') {
    return { kind: 'bookmark', name: selectedTarget.name };
  }

  return { kind: 'paragraph', path: selectedTarget.path };
}
